<template>
  <!-- Kit ••• menu: one shared menu for a row or a header. Items are
       { title, icon, onClick, danger, dim, disabled, divider }. On a mouse
       it is the Mac boards' menu (MacMenus, MacContext): text-only rows,
       labels 15 px in, an optional caption line. On a touchscreen the rows
       are 46 tall and keep their icons. -->
  <v-menu
    v-model="open"
    :location="cursor ? 'bottom start' : 'bottom end'"
    :target="cursor || undefined"
    :close-on-content-click="true"
  >
    <template v-slot:activator="{ props }">
      <button
        v-bind="props"
        class="more-btn"
        type="button"
        :aria-label="label"
        :style="{ width: size + 'px', height: size + 'px' }"
      >
        <v-icon :size="Math.round(size * 0.56)">mdi-dots-horizontal</v-icon>
      </button>
    </template>
    <v-list
      density="compact"
      class="cl-overflow-menu more-menu"
      :class="{ 'is-compact': compact }"
      :style="width ? { '--more-w': width + 'px' } : null"
    >
      <div v-if="caption" class="more-caption">{{ caption }}</div>
      <template v-for="(item, i) in items" :key="i">
        <v-divider v-if="item.divider" class="more-divider" />
        <v-list-item
          v-else
          :disabled="item.disabled"
          :class="{ 'more-danger': item.danger, 'more-dim': item.dim }"
          @click="choose(item)"
        >
          <template v-if="item.icon" v-slot:prepend>
            <v-icon size="18">{{ item.icon }}</v-icon>
          </template>
          <v-list-item-title>{{ item.title }}</v-list-item-title>
        </v-list-item>
      </template>
    </v-list>
  </v-menu>
</template>

<script>
export default {
  name: "MoreMenu",
  props: {
    items: { type: Array, required: true },
    label: { type: String, default: "More" },
    size: { type: Number, default: 32 },
    // A quiet first line naming what the menu is about (MacContext:
    // "Maya's loop · F♯m · 4.0 s").
    caption: { type: String, default: "" },
    // The Session menu's 22 px rows (MacMenus); the loop menu's are 24.
    compact: { type: Boolean, default: false },
    // The panel's width on a mouse (MacMenus 292, MacContext 236).
    width: { type: Number, default: 0 },
  },
  emits: ["update:open"],
  data: () => ({ open: false, cursor: null }),
  watch: {
    open(v) {
      if (!v) this.cursor = null;
      this.$emit("update:open", v);
    },
  },
  methods: {
    // Close first, then act, so whatever the item opens (a dialog, a field
    // to type in) isn't fighting a menu that is still up.
    choose(item) {
      this.open = false;
      if (item.onClick) item.onClick();
    },
    // Open from somewhere other than the ••• (a tap on the title).
    show() {
      this.open = true;
    },
    // Open at the pointer (a right-click on a row).
    openAt(x, y) {
      this.cursor = [x, y];
      this.open = true;
    },
  },
};
</script>

<style scoped>
.more-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: var(--cl-control-fill);
  color: var(--cl-label);
  cursor: pointer;
  flex-shrink: 0;
}
.more-btn:hover {
  background: var(--cl-control-fill-strong);
}
</style>

<style>
.more-menu .more-danger .v-list-item-title,
.more-menu .more-danger .v-icon {
  color: var(--cl-destructive-text) !important;
}
.more-menu .more-dim .v-list-item-title {
  color: var(--cl-label-2) !important;
}
.more-menu .more-divider {
  display: block !important;
  margin: 4px 8px;
  border-color: var(--cl-separator) !important;
  opacity: 1;
}
.more-menu .more-caption {
  padding: 8px 16px 4px;
  font: var(--cl-caption);
  color: var(--cl-label-45);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Mouse: the Mac boards' menus. 24 px text-only rows (22 in the Session
   menu) at 13 px, padding 0 10 inside a 5 px panel, so labels sit 15 px in;
   hairlines at .12. */
@media (pointer: fine) {
  .cl-overflow-menu.more-menu {
    padding: 5px !important;
    border-color: var(--border-default) !important;
    border-radius: var(--cl-r-md) !important;
    width: var(--more-w, auto);
    min-width: 180px;
  }
  .cl-overflow-menu.more-menu .v-list-item {
    min-height: var(--cl-menu-row) !important;
    height: var(--cl-menu-row);
    padding: 0 10px !important;
    border-radius: 5px !important;
  }
  .cl-overflow-menu.more-menu.is-compact .v-list-item {
    min-height: 22px !important;
    height: 22px;
  }
  .cl-overflow-menu.more-menu .v-list-item__prepend {
    display: none;
  }
  .cl-overflow-menu.more-menu .more-caption {
    padding: 3px 10px 5px;
  }
  .cl-overflow-menu.more-menu .v-list-item-title {
    font: 400 13px/16px var(--cl-font) !important;
  }
  .cl-overflow-menu.more-menu .more-divider {
    margin: 4px 8px;
    border-color: var(--border-default) !important;
  }
  .cl-overflow-menu.more-menu.is-compact .more-divider {
    margin: 5px 8px;
  }
}
</style>
